import React, { createContext, useState, useMemo, useCallback, useEffect } from 'react';
import axiosInstance from '../services/api';

const AuthContext = createContext(null);

// Duração do logout em milissegundos (3 horas)
const LOGOUT_TIME = 3 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } });
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('accessToken'));
  const [mustChangePassword, setMustChangePassword] = useState(() => { try { return JSON.parse(localStorage.getItem('mustChangePassword')) || false; } catch { return false; } });

  const logout = useCallback(() => {
    localStorage.clear();
    setUser(null);
    setIsAuthenticated(false);
    setMustChangePassword(false);
    // Redireciona para a página de login após o logout
    window.location.href = '/';
  }, []);

  // Efeito para gerenciar o logout por inatividade
  useEffect(() => {
    let inactivityTimer;

    const resetTimer = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (!isAuthenticated) return;

      inactivityTimer = setTimeout(() => {
        alert("Sua sessão expirou por inatividade. Você será deslogado.");
        logout();
      }, LOGOUT_TIME);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    if (isAuthenticated) {
      events.forEach(event => window.addEventListener(event, resetTimer));
      resetTimer();
    }

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [isAuthenticated, logout]);

  const login = useCallback(async (email, password, recaptchaToken) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/api/token/', { email, password, recaptchaToken });
      localStorage.setItem('accessToken', response.data.access);
      localStorage.setItem('refreshToken', response.data.refresh);

      const userResponse = await axiosInstance.get('/api/v1/account/me/');
      const userData = userResponse.data;

      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);

      if (userData.must_change_password) {
        setMustChangePassword(true);
        localStorage.setItem('mustChangePassword', 'true');
      } else {
        setMustChangePassword(false);
        localStorage.removeItem('mustChangePassword');
      }
      return { success: true };
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('alterar sua senha temporária')) {
        // Guarda o token para poder fazer a chamada de troca de senha
        localStorage.setItem('accessToken', error.response.config.headers.Authorization?.split(' ')[1] || '');
        setIsAuthenticated(true);
        setMustChangePassword(true);
        localStorage.setItem('mustChangePassword', 'true');
        // Cria um objeto de usuário temporário para evitar que 'user' seja null
        setUser({ email: email, first_name: 'Novo Usuário', department: 'temp' });
        return { success: true };
      }
      localStorage.clear();
      setIsAuthenticated(false);
      setUser(null);
      setMustChangePassword(false);
      return { success: false, message: error.response?.data?.detail || 'Credenciais inválidas.' };
    } finally {
      setLoading(false);
    }
  }, []);

  const passwordHasBeenChanged = useCallback(async () => {
    try {
      const userResponse = await axiosInstance.get('/api/v1/account/me/');
      const userData = userResponse.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setMustChangePassword(false);
      localStorage.removeItem('mustChangePassword');
    } catch (error) {
      console.error("Erro ao recarregar dados do usuário. Deslogando.", error);
      logout();
    }
  }, [logout]);

  const value = useMemo(() => ({ user, loading, isAuthenticated, mustChangePassword, login, logout, passwordHasBeenChanged }), [user, loading, isAuthenticated, mustChangePassword, login, logout, passwordHasBeenChanged]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
export default AuthContext;